/**
 * Home2 Landing Page
 * Modern, SEO-optimized landing page focused on the website builder restriction problem.
 * 
 * @module components/home2/Home2
 */

import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Sparkles } from 'lucide-react';
import HamburgerMenu from '../HamburgerMenu';
import SEO, { generateFAQSchema } from '../SEO';
import Home2Hero from './Home2Hero';
import Home2Problem from './Home2Problem';
import Home2Compiler from './Home2Compiler';
import Home2Demo from './Home2Demo';
import Home2FAQ from './Home2FAQ';
import Home2CTA from './Home2CTA';
import { FAQ_DATA } from '../../data/home2Content';

const Home2 = () => {
  const navigate = useNavigate();
  const howItWorksRef = useRef(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePrimaryCTA = () => {
    navigate('/sample-gallery');
  };

  // Generate FAQ schema for SEO
  const faqSchema = generateFAQSchema(FAQ_DATA);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* SEO Meta Tags */}
      <SEO 
        title="TweePorter - Add Interactive Stories to GoDaddy, Wix & Squarespace"
        description="Convert Twine stories to clean HTML that works on any website builder. No scripts, no plugins, no hosting required. Free tool for interactive content."
        canonicalUrl="/home2"
        structuredData={faqSchema}
      />
      
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex items-center justify-between">
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate('/')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
          aria-label="Go to homepage"
        >
          <BookOpen className="h-8 w-8 text-indigo-600" />
          <div>
            <span className="text-2xl font-bold text-slate-900">TweePorter.ca</span>
            <div className="flex items-center space-x-1 text-xs text-indigo-600 font-medium">
              <Sparkles className="h-3 w-3" />
              <span>Optimized for SugarCube</span>
            </div>
          </div>
        </div>
        <HamburgerMenu />
      </header>

      {/* Hero Section */}
      <Home2Hero 
        onPrimaryCTA={handlePrimaryCTA}
        onSecondaryCTA={scrollToHowItWorks}
      />

      {/* Problem Section */}
      <Home2Problem />

      {/* Compiler Steps Infographic */}
      <div ref={howItWorksRef}>
        <Home2Compiler />
      </div>

      {/* Demo Story */}
      <Home2Demo />

      {/* FAQ Section */}
      <Home2FAQ />

      {/* CTA Footer */}
      <Home2CTA />

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-slate-200">
        <div className="text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} TweePorter. Free tool for interactive storytelling.</p>
          <p className="mt-1">
            Built for creators who refuse to be limited by their website builder.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home2;
